import { useFeatureFlagPayload } from "posthog-js/react"
import {
    createContext,
    Reducer,
    useContext,
    useEffect,
    useReducer,
} from "react"
import { FeatureFlags } from "types/FeatureFlags"
import {
    ICartState,
    IDayType,
    ImprovedCartActionsEnum,
    IReducerActions,
    ISelectedTicket,
    SupportedPaymentMethods,
} from "types/improved-booking"
import { isCountCurrentlyQualifiedForPromotion } from "utils/helpers/bookingv2/ticket"

interface Props {
    children: React.ReactNode
}
type FeatureFlagPayload = {
    events?: string[]
}
export function serializeCartData(cartData) {
    return encodeURIComponent(JSON.stringify(cartData))
}

export function deserializeCartData(serializedCartData) {
    return JSON.parse(decodeURIComponent(serializedCartData))
}

const improvedInitialCartValues: ICartState = {
    event: "",
    event_id: undefined,
    eventSlug: undefined,
    total: 0,
    selectedTickets: [],
    selectedDays: null,
    selectedSeats: [],
    selectedExtras: [],
    finalCart: [],
    coupon: undefined,
    guestData: undefined,
    bookingStep: "PrivacyPolicy",
    accreditation: null,
    payment_method: "card",
    user_type: "user",
    eventTitle: "",
    availablePaymentMethods: ["card", "tabby"],
}

const toTimestamp = (value?: Date | string) => {
    if (!value) return null

    const timestamp = new Date(value).getTime()
    return Number.isNaN(timestamp) ? null : timestamp
}

const isSameSelectedDay = (
    currentDay: ICartState["selectedDays"],
    nextDay: ICartState["selectedDays"]
) => {
    if (!currentDay || !nextDay) return false

    const currentStart = toTimestamp(currentDay.start)
    const currentEnd = toTimestamp(currentDay.end)
    const nextStart = toTimestamp(nextDay.start)
    const nextEnd = toTimestamp(nextDay.end)

    return (
        currentStart === nextStart &&
        currentEnd === nextEnd &&
        (currentDay.timeslot_id === nextDay.timeslot_id ||
            !currentDay.timeslot_id ||
            !nextDay.timeslot_id)
    )
}

const mergeSelectedDay = (
    currentDay: ICartState["selectedDays"],
    nextDay: NonNullable<ICartState["selectedDays"]>
) => {
    if (!currentDay) {
        return nextDay
    }

    // Do not let a placeholder day overwrite an already selected timeslot,
    // but only preserve data when the day is the same (same start/end dates).
    // Otherwise a user going back to SELECT_DAY and picking a different day
    // would inherit the previous day's timeslot_id and ticket types.
    const sameDay = isSameSelectedDay(currentDay, nextDay)
    if (!nextDay.timeslot_id && currentDay.timeslot_id && sameDay) {
        return {
            ...nextDay,
            timeslot_id: currentDay.timeslot_id,
            types: currentDay.types,
            seats_season_id: currentDay.seats_season_id,
            allow_using_coupon: currentDay.allow_using_coupon,
        }
    }

    return nextDay
}

const CartReducer: Reducer<ICartState, IReducerActions> = (state, action) => {
    switch (action.type) {
        case ImprovedCartActionsEnum.ADD_EVENT:
            return {
                ...state,
                event: action.payload.event,
                eventSlug: action.payload.slug || state.eventSlug,
            }
        case ImprovedCartActionsEnum.SET_EVENT_TITLE:
            return {
                ...state,
                eventTitle: action.payload,
            }
        case ImprovedCartActionsEnum.ADD_DAY: {
            const shouldPreserveSelection = isSameSelectedDay(
                state.selectedDays,
                action.payload
            )
            const nextSelectedDay = mergeSelectedDay(
                state.selectedDays,
                action.payload
            )

            return {
                ...state,
                selectedDays: nextSelectedDay,
                selectedTickets: shouldPreserveSelection
                    ? state.selectedTickets
                    : [],
                selectedSeats: shouldPreserveSelection
                    ? state.selectedSeats
                    : [],
                selectedExtras: shouldPreserveSelection
                    ? state.selectedExtras
                    : [],
                coupon: shouldPreserveSelection ? state.coupon : undefined,
            }
        }

        case ImprovedCartActionsEnum.ADD_TICKET: {
            const incomingSelectedTicket = action.payload as ISelectedTicket

            const ticketDefinition = state.selectedDays?.types.find(
                (typeDef: any) => {
                    const typeDefMasterId =
                        typeof typeDef.type_id === "object" &&
                        typeDef.type_id !== null &&
                        typeof typeDef.type_id._id === "string"
                            ? typeDef.type_id._id
                            : typeDef.type_id

                    return typeDefMasterId === incomingSelectedTicket.type_id
                }
            )

            if (!ticketDefinition) {
                console.error(
                    "[Debug CartReducer ADD_TICKET] Could not find ticket definition for type_id:",
                    incomingSelectedTicket.type_id,
                    "Available types (raw):",
                    state.selectedDays?.types
                        ? JSON.parse(JSON.stringify(state.selectedDays.types))
                        : "undefined"
                )
                return state
            }

            const currentTicketCount = state.selectedTickets.filter(
                (ticket) => ticket.type_id === incomingSelectedTicket.type_id
            ).length

            const ticketForPromoCheck: ISelectedTicket = {
                ...incomingSelectedTicket,
                promotion: ticketDefinition.promotion,
            }

            const isPromotionApplicable =
                ticketDefinition.promotion &&
                isCountCurrentlyQualifiedForPromotion(
                    ticketForPromoCheck,
                    currentTicketCount
                )

            let ticketToAdd = {
                ...incomingSelectedTicket,
                promotion: ticketDefinition.promotion,
            }

            if (isPromotionApplicable) {
                ticketToAdd = {
                    ...ticketToAdd,
                    price: 0,
                    is_promotion_free: true,
                }
            } else {
                ticketToAdd = {
                    ...ticketToAdd,
                    price: ticketDefinition.price,
                    is_promotion_free: false,
                }
            }

            return {
                ...state,
                selectedTickets: [...state.selectedTickets, ticketToAdd],
            }
        }

        case ImprovedCartActionsEnum.ADD_ALL_SELECTED_TICKETS:
            return {
                ...state,
                selectedTickets: action.payload,
            }
        case ImprovedCartActionsEnum.REMOVE_DAY:
            return {
                ...state,
                selectedDays: null,
                selectedTickets: [],
                selectedSeats: [],
                selectedExtras: [],
                coupon: undefined,
            }
        case ImprovedCartActionsEnum.ADD_SEATS: {
            const check = state.selectedSeats.find(
                (seat) => seat.id === action.payload.id
            )

            if (check) {
                return {
                    ...state,
                    selectedSeats: state.selectedSeats.filter(
                        (seat) => seat.id !== action.payload.id
                    ),
                }
            } else {
                return {
                    ...state,
                    selectedSeats: [...state.selectedSeats, action.payload],
                }
            }
        }
        case ImprovedCartActionsEnum.ADD_COUPON:
            return {
                ...state,
                coupon: action.payload,
            }
        case ImprovedCartActionsEnum.REMOVE_COUPON:
            return {
                ...state,
                coupon: undefined,
            }
        case ImprovedCartActionsEnum.REMOVE_TICKETS:
            return {
                ...state,
                selectedTickets: [],
            }
        case ImprovedCartActionsEnum.REMOVE_TICKET: {
            const id = [...state.selectedTickets]
                .reverse()
                .findIndex((t) => t.type_id === action.payload.type_id)

            if (id === -1) {
                return state
            }

            const reverseIndex = state.selectedTickets.length - 1 - id
            const newTickets = [...state.selectedTickets]
            newTickets.splice(reverseIndex, 1)

            return {
                ...state,
                selectedTickets: newTickets,
            }
        }

        case ImprovedCartActionsEnum.REMOVE_SEAT:
            return {
                ...state,
                selectedSeats: state.selectedSeats.filter(
                    (seat) => seat.id !== action.payload.id
                ),
            }
        case ImprovedCartActionsEnum.REMOVE_SEATS:
            return {
                ...state,
                selectedSeats: [],
            }
        case ImprovedCartActionsEnum.CLEAR_CART:
            return { ...state, ...improvedInitialCartValues }
        case ImprovedCartActionsEnum.SET_GUEST_DATA:
            return {
                ...state,
                guestData: action.payload,
            }
        case ImprovedCartActionsEnum.SET_BOOKING_STEP:
            return {
                ...state,
                bookingStep: action.payload,
            }
        case ImprovedCartActionsEnum.SET_ACCREDITATION_DATA:
            return {
                ...state,
                accreditation: action.payload,
            }

        case ImprovedCartActionsEnum.SET_PAYMENT_METHOD:
            return {
                ...state,
                payment_method: action.payload,
            }
        case ImprovedCartActionsEnum.REMOVE_PAYMENT_METHOD_ACTION:
            return {
                ...state,
                payment_method: "card",
            }

        case ImprovedCartActionsEnum.SET_USER_TYPE:
            return {
                ...state,
                user_type: action.payload,
            }

        case ImprovedCartActionsEnum.SET_AVAILABLE_PAYMENT_METHODS_ACTION: {
            return {
                ...state,
                // available_payment_methods: action.payload,
                availablePaymentMethods: action.payload,
            }
        }
        case ImprovedCartActionsEnum.REMOVE_AVAILABLE_PAYMENT_METHODS_ACTION: {
            const availablePaymentMethods =
                state.availablePaymentMethods.filter(
                    (method) => method !== action.payload
                )
            return {
                ...state,
                availablePaymentMethods: availablePaymentMethods,
                payment_method: availablePaymentMethods.includes(
                    state.payment_method!
                )
                    ? state.payment_method
                    : "card",
            }
        }

        case ImprovedCartActionsEnum.ADD_EXTRA: {
            const existingExtraIndex = state.selectedExtras.findIndex(
                (extra) => extra.extra_id === action.payload.extra_id
            )

            if (existingExtraIndex !== -1) {
                // Increase existing extra quantity
                const updatedExtras = [...state.selectedExtras]
                updatedExtras[existingExtraIndex] = {
                    ...updatedExtras[existingExtraIndex],
                    qty:
                        updatedExtras[existingExtraIndex].qty +
                        action.payload.qty,
                }
                return {
                    ...state,
                    selectedExtras: updatedExtras,
                }
            } else {
                // Add new extra
                return {
                    ...state,
                    selectedExtras: [...state.selectedExtras, action.payload],
                }
            }
        }

        case ImprovedCartActionsEnum.REMOVE_EXTRA: {
            const existingExtraIndex = state.selectedExtras.findIndex(
                (extra) => extra.extra_id === action.payload.extra_id
            )

            if (existingExtraIndex === -1) {
                return state
            }

            const currentExtra = state.selectedExtras[existingExtraIndex]

            if (currentExtra.qty <= 1) {
                return {
                    ...state,
                    selectedExtras: state.selectedExtras.filter(
                        (extra) => extra.extra_id !== action.payload.extra_id
                    ),
                }
            }

            const updatedExtras = [...state.selectedExtras]
            updatedExtras[existingExtraIndex] = {
                ...currentExtra,
                qty: currentExtra.qty - 1,
            }

            return {
                ...state,
                selectedExtras: updatedExtras,
            }
        }

        case ImprovedCartActionsEnum.REMOVE_EXTRAS: {
            return {
                ...state,
                selectedExtras: [],
            }
        }

        default:
            return state
    }
}
export const CartDispatchContext = createContext<
    React.Dispatch<IReducerActions>
>((action: IReducerActions) => action)

export const CartContext = createContext<ICartState>(improvedInitialCartValues)

export const CartProvider: React.FC<Props> = ({ children }) => {
    const [state, dispatch] = useReducer(
        CartReducer,
        improvedInitialCartValues,
        (initial) => {
            if (typeof window !== "undefined") {
                const storedCart = localStorage.getItem("pass_ticket_cart")
                if (storedCart) {
                    return JSON.parse(storedCart)
                } else {
                    localStorage.setItem(
                        "pass_ticket_cart",
                        JSON.stringify(initial)
                    )
                    return initial
                }
            }
            return initial
        }
    )

    const featureFlagPayload = useFeatureFlagPayload(
        FeatureFlags.TABBY
    ) as FeatureFlagPayload

    const flagEvents = featureFlagPayload?.events || []

    const isTabbySupported = flagEvents.includes(state.event_id!)

    const availablePaymentMethods = () => {
        if (isTabbySupported) {
            return ["card", "tabby"]
        }
        return ["card"]
    }

    useEffect(() => {
        dispatch({
            type: ImprovedCartActionsEnum.SET_AVAILABLE_PAYMENT_METHODS_ACTION,
            payload: availablePaymentMethods() as SupportedPaymentMethods[],
        })
    }, [isTabbySupported])

    useEffect(() => {
        if (typeof window !== "undefined") {
            localStorage.setItem("pass_ticket_cart", JSON.stringify(state))
        }
    }, [state])

    return (
        <CartContext.Provider value={state}>
            <CartDispatchContext.Provider value={dispatch}>
                {children}
            </CartDispatchContext.Provider>
        </CartContext.Provider>
    )
}

export const useCartState = () => {
    const context = useContext(CartContext)
    if (context === undefined) {
        throw new Error("useCartState must be used within a CartProvider")
    }
    return context
}
export const useCartDispatch = () => {
    const context = useContext(CartDispatchContext)
    if (context === undefined) {
        throw new Error("useCartDispatch must be used within a CartProvider")
    }
    return context
}
